import { AdElement } from '../model/types';

export type AlignMode = 'left' | 'centerX' | 'right' | 'top' | 'centerY' | 'bottom';
export type DistributeAxis = 'horizontal' | 'vertical';

export interface ElementPatch {
  id: string;
  x?: number;
  y?: number;
}

interface Bounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

const boundsOf = (elements: AdElement[]): Bounds => ({
  left: Math.min(...elements.map((element) => element.x)),
  right: Math.max(...elements.map((element) => element.x + element.width)),
  top: Math.min(...elements.map((element) => element.y)),
  bottom: Math.max(...elements.map((element) => element.y + element.height)),
});

/**
 * Wyrównanie zaznaczonych elementów WZGLĘDEM SIEBIE, do wspólnej ramki
 * zaznaczenia. To coś innego niż wyrównanie do planszy: tam punktem odniesienia
 * jest kadr, tutaj skrajne elementy grupy.
 */
export const alignElements = (elements: AdElement[], mode: AlignMode): ElementPatch[] => {
  if (elements.length < 2) return [];
  const bounds = boundsOf(elements);

  return elements.map((element) => {
    switch (mode) {
      case 'left':
        return { id: element.id, x: Math.round(bounds.left) };
      case 'centerX':
        return { id: element.id, x: Math.round((bounds.left + bounds.right) / 2 - element.width / 2) };
      case 'right':
        return { id: element.id, x: Math.round(bounds.right - element.width) };
      case 'top':
        return { id: element.id, y: Math.round(bounds.top) };
      case 'centerY':
        return { id: element.id, y: Math.round((bounds.top + bounds.bottom) / 2 - element.height / 2) };
      default:
        return { id: element.id, y: Math.round(bounds.bottom - element.height) };
    }
  });
};

/**
 * Równy rozkład elementów między skrajnymi.
 *
 * Skrajne zostają na miejscu, a środkowe dostają równe odstępy liczone od
 * krawędzi, nie od środków: przy różnej wielkości elementów tylko odstęp
 * wygląda na równy, wyrównanie środków już nie.
 */
export const distributeElements = (elements: AdElement[], axis: DistributeAxis): ElementPatch[] => {
  if (elements.length < 3) return [];

  const horizontal = axis === 'horizontal';
  const sorted = [...elements].sort((a, b) => (horizontal ? a.x - b.x : a.y - b.y));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const span = horizontal
    ? (last.x + last.width) - first.x
    : (last.y + last.height) - first.y;
  const used = sorted.reduce((sum, element) => sum + (horizontal ? element.width : element.height), 0);
  const gap = (span - used) / (sorted.length - 1);

  let cursor = horizontal ? first.x : first.y;

  return sorted.map((element) => {
    const position = Math.round(cursor);
    cursor += (horizontal ? element.width : element.height) + gap;
    return horizontal ? { id: element.id, x: position } : { id: element.id, y: position };
  });
};
